import { isDefined, isNotDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectOrderEntryByIdFromBlotter,
  selectTakeProfitErrors,
  selectTakeProfitNotionalCurrencyIndex,
  selectTradeIdFromTakeProfitTile,
} from '@/store/orders/takeProfitOrder/selectors.ts';

type TakeProfitNotionalProps = {
  notional: number | undefined;
  setNotional: (value: number | undefined) => void;
  setLimitPrice: (value: number | undefined) => void;
};

export const TakeProfitEditingNotional = (props: TakeProfitNotionalProps) => {
  const track = useTrackEvent();
  const { notional, setNotional } = props;
  const { tileId, setIsModifyingField } = useContext(TakeProfitContext);
  const notionalCurrencyIndex = useAppSelector((state) => selectTakeProfitNotionalCurrencyIndex(state, tileId));

  const tradeId = useAppSelector((state) => selectTradeIdFromTakeProfitTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const currency = isDefined(orderFromBlotter) ? orderFromBlotter?.notionalCurrency : undefined;
  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const notionalError = errors?.notional;

  const validateTakeProfit = useValidateTakeProfit();

  const handleNotionalChange = async (value: number | undefined) => {
    track('Order action', 'Notional changed');
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : ['', ''];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleNotionalChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateTakeProfit({
        notional: value,
        notionalCurrencyIndex: currency === currency1 ? 1 : 2,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setNotional(value);

        if (isDefined(response?.data?.limitPrice)) {
          props.setLimitPrice(response.data.limitPrice);
        }
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.notional.label">
      <OmsFieldErrorWrapper errors={notionalError}>
        <SgAmountInput
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          size="sm"
          value={
            notional ?? (isDefined(orderFromBlotter?.notional) ? (orderFromBlotter?.notional as number) : undefined)
          }
          data-e2e="notional"
          onChange={handleNotionalChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          unit={currency}
          state={isDefined(notionalError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
